import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageUploadInput from "../../../../parts/inputs/image-upload";
import Select from "../../../../parts/inputs/select";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import {
  resolveSplitType,
  SPLIT_FORCED,
  SPLIT_SUGGESTED,
} from "../../../../utils/flexible-program";
import getCycleCardImagePath from "../../../../utils/get-cycle-card-image-path";

const WORKOUTS_PER_WEEK = Array.from({ length: 7 }, (_, i) => ({
  text: String(i + 1),
  value: i + 1,
}));

/**
 * Everything about the program that isn't a week-by-week edit: identity
 * (name, thumbnail), how many D-slots a NEW week gets, and the training
 * split. No description/intro video field here (or on StartFlexibleProgram.js,
 * which this was trimmed to match) - whatever a program was created with
 * stays stored and keeps rendering wherever it already showed, but neither
 * modal offers a way to set or change it anymore.
 *
 * A local-state modal, not the usual ?modal[type]=...-driven one (parts/
 * modal-new's Modal): that convention round-trips the WHOLE page through
 * flexible-program.controller.js (re-fetching editCycle, flexibleProgramOverview,
 * ...) just to open or close a dialog. This copies WeekDayModal's pattern
 * instead (calendar/components/WeekDayModal.js) - a plain client-side overlay,
 * opened/closed via a prop the parent's own useState controls, with no
 * network involved until something is actually saved.
 *
 * A single form with one Save, not the Manager top panel's per-field
 * auto-save links - this is the comprehensive edit surface, the top panel is
 * the quick one. First day of week is read-only: it anchors the occurrence
 * lattice every future week is measured from, so changing it after the first
 * week is authored would desync the two.
 *
 * Repeat lives on each WEEK ROW in the manager now, not here - a program can
 * carry many weeks over its life, and only the live one's flag ever governs,
 * so a single program-level switch could no longer say which week it meant.
 * Stopping the program is also gone from here - the header's own "Stop
 * program" button is the one place that happens.
 *
 * A real `<form encType="multipart/form-data">`, not the usual submit-form
 * helper: that helper flattens its body into hidden textareas, which cannot
 * carry a File the thumbnail picker produces. Every field below still keeps
 * its own name attribute in step with its React-controlled value.
 *
 * Takes `cycle`/`athlete` directly rather than reading `locals.editCycle` -
 * the Programs-page card opens this as a plain local modal (no navigation,
 * no flexible-program.controller.js re-fetch), so there is no `editCycle` in
 * scope there; FlexibleProgram.js still passes its own `cycle`/`athlete`.
 */
const FlexibleSettings = ({ cycle, athlete, onClose }) => {
  const { t } = useTranslation("coaching-portal");

  const [name, setName] = useState(cycle?.name || "");
  const [sessionsPerWeek, setSessionsPerWeek] = useState(
    cycle?.sessionsPerWeek || 3
  );
  const [trainingSplitType, setTrainingSplitType] = useState(
    resolveSplitType(cycle)
  );
  // Read once at mount: the server needs to know what the split WAS to decide
  // whether it also has to re-stamp the live week, not what it currently is.
  const [previousTrainingSplitType] = useState(resolveSplitType(cycle));

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="wk-modal-overlay open" onClick={onClose}>
      {/* The form IS the modal box (not just body+footer): parts/modals/
          add-cycle.js wraps its whole Modal the same way, and .wk-modal's
          layout is selected purely by class, so a <form> in its place needs
          no CSS change. data-noreset matches every other multipart cycle
          form in this app.

          Rill's browser adapter hijacks every real <form> submit (window
          "submit" listener) and refreshes the page's locals in place rather
          than navigating - so unlike add-cycle.js's ?modal[type]=...-driven
          modal (which closes itself because the refreshed query clears that
          param), this LOCAL-STATE modal stays mounted with `onClose` never
          called unless we call it ourselves. Closing on submit rather than
          waiting for the response matches the fire-and-forget close other
          local modals here use (FlexibleStop). */}
      <form
        className="wk-modal flexible-settings-modal"
        action={`/actions/cycle/${cycle._id}/flexible-settings?athlete=${athlete || ""}`}
        method="POST"
        encType="multipart/form-data"
        data-noreset
        onClick={(event) => event.stopPropagation()}
        onSubmit={onClose}
      >
        <input
          type="hidden"
          name="previousTrainingSplitType"
          value={previousTrainingSplitType}
        />

        <header className="wk-modal-header">
          <h2 className="alt">{t("flexible.settings.title")}</h2>
          <button
            type="button"
            className="wk-modal-close"
            data-button-type="tertiary"
            data-icon-only
            onClick={onClose}
            aria-label={t("flexible.manager.cancel")}
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
        </header>

        <div className="wk-modal-body gap-2!">
          <div className="group">
            <label htmlFor="flexible-settings-name">
              {t("flexible.manager.programName")}
            </label>
            <input
              id="flexible-settings-name"
              name="name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          {/* workouts per week | first day of week - same pairing as the
              start modal. Each field's explanation moved from a subtitle
              under it into a (?) tooltip next to its label, same as the
              start modal's own sessions/first-day/start-date row
              (StartFlexibleProgram.js). */}
          <div className="grid grid-cols-2 gap-4">
            <div className="group">
              <div className="flex flex-row items-center gap-1 mb-1">
                <label className="mb-0!">
                  {t("flexible.settings.workoutsPerWeek")}
                </label>
                <ToolTip
                  body={t("flexible.settings.workoutsPerWeekHint")}
                  id="flexible-settings-sessions-info"
                >
                  <SvgIcon
                    alt={t("flexible.settings.infoIconAlt")}
                    src="/img/new-icons/question-circle.svg"
                  />
                </ToolTip>
              </div>
              <Select
                name="sessionsPerWeek"
                options={WORKOUTS_PER_WEEK}
                value={sessionsPerWeek}
                onChange={(value) => setSessionsPerWeek(Number(value))}
                overlay={true}
              />
            </div>

            <div className="group">
              <div className="flex flex-row items-center gap-1 mb-1">
                <label className="mb-0!">
                  {t("flexible.settings.firstDayOfWeek")}
                </label>
                <ToolTip
                  body={t("flexible.settings.firstDayOfWeekLocked")}
                  id="flexible-settings-first-day-info"
                >
                  <SvgIcon
                    alt={t("flexible.settings.infoIconAlt")}
                    src="/img/new-icons/question-circle.svg"
                  />
                </ToolTip>
              </div>
              <p className="static-field">
                {moment()
                  .day(cycle?.firstDayOfWeek ?? 1)
                  .format("dddd")}
              </p>
              <input
                type="hidden"
                name="firstDayOfWeek"
                value={cycle?.firstDayOfWeek ?? 1}
              />
            </div>
          </div>

          <div className="group">
            <ImageUploadInput
              name="image"
              className="program-image"
              defaultValue={getCycleCardImagePath(cycle)}
              raw
            />
          </div>

          {/* training split - same radio-card control as the Program
              Manager's own training-type selector (FlexibleProgramManager.js
              - .flexible-split-options/.flexible-split-option, stacked here
              instead of side by side, same as StartFlexibleProgram.js - see
              flexible-settings-modal.css) and the same control that modal
              now uses, not the Manager's own <a role="radio">: a click there
              is a real navigate-and-save, which would fire mid-edit here and
              lose whatever else is unsaved in this form, so selection stays
              local state behind a native <label>/<input type="radio"> pair
              instead - a hidden input mirrors it for the native submit. The
              divider is this group's own top border rather than a separate
              gapped sibling - a standalone divider div would add a second
              full gap-4 on top of the one already above it (32px, not the
              intended 16px). */}
          <div className="group border-t border-(--white-10)">
            <input
              type="hidden"
              name="trainingSplitType"
              value={trainingSplitType}
            />
            <div className="flexible-split-options">
              {[SPLIT_SUGGESTED, SPLIT_FORCED].map((mode) => {
                const active = trainingSplitType === mode;
                return (
                  <label key={mode} className="flexible-split-option">
                    <div className="input-wrapper">
                      <input
                        type="radio"
                        checked={active}
                        onChange={() => setTrainingSplitType(mode)}
                      />
                      <span className="radio-input">
                        <span className="radio" />
                      </span>
                    </div>
                    <span className="flexible-split-option-text">
                      <span className="flex items-center gap-2">
                        <span className="font-medium">
                          {t(`flexible.split.${mode}`)}
                        </span>
                        {mode === SPLIT_SUGGESTED && (
                          <span className="text-xs px-2! py-0.5! rounded-full bg-(--white-10) text-(--white-70)">
                            {t("flexible.default")}
                          </span>
                        )}
                      </span>
                      <span className="text-[0.75rem] opacity-60">
                        {t(`flexible.splitDesc.${mode}`)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* A sibling of .wk-modal-body, not part of it: that div is the only
            scrollable region (overflow-y-auto), so keeping Save/Cancel
            outside it is what keeps them always visible instead of requiring
            a scroll to reach - same header/body/footer split as the standard
            Modal-Box shell this replaces. */}
        <footer className="wk-modal-footer">
          <button type="button" data-button-type="secondary" onClick={onClose}>
            {t("flexible.manager.cancel")}
          </button>
          <button type="submit" data-button-type="primary">
            {t("flexible.manager.save")}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default FlexibleSettings;
